import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { crmService } from '../../../services/crmService';


const CRMPanel = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientHistory, setClientHistory] = useState(null);
  const [upcomingFollowUps, setUpcomingFollowUps] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    segment: 'new',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedSegment]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [clientsData, followUpsData] = await Promise.all([
        crmService?.getClientsBySegment(selectedSegment),
        crmService?.getUpcomingFollowUps(),
      ]);
      setClients(clientsData);
      setUpcomingFollowUps(followUpsData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadClientHistory = async (clientId) => {
    try {
      const history = await crmService?.getClientHistory(clientId);
      setClientHistory(history);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleViewClient = async (client) => {
    setSelectedClient(client);
    await loadClientHistory(client?.id);
  };

  const handleOpenModal = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client?.name || '',
        email: client?.email || '',
        phone: client?.phone || '',
        segment: client?.segment || 'new',
        notes: client?.notes || ''
      });
    } else {
      setEditingClient(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        segment: 'new',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingClient(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    try {
      if (editingClient) {
        await crmService?.updateClient(editingClient?.id, formData);
      } else {
        await crmService?.createClient(formData);
      }
      handleCloseModal();
      loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client?')) return;

    try {
      await crmService?.deleteClient(clientId);
      loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const segmentOptions = [
    { value: 'all', label: 'Tous les clients' },
    { value: 'new', label: 'Nouveaux' },
    { value: 'active', label: 'Actifs' },
    { value: 'vip', label: 'VIP' },
    { value: 'inactive', label: 'Inactifs' },
    { value: 'churned', label: 'Perdus' },
  ];

  const getSegmentColor = (segment) => {
    const colors = {
      new: 'var(--color-trust)',
      active: 'var(--color-success)',
      vip: 'var(--color-primary)',
      inactive: 'var(--color-warning)',
      churned: 'var(--color-error)',
    };
    return colors?.[segment] || 'var(--color-muted-foreground)';
  };

  const getSegmentIcon = (segment) => {
    const icons = {
      new: 'UserPlus',
      active: 'Users',
      vip: 'Crown',
      inactive: 'UserX',
      churned: 'UserMinus',
    };
    return icons?.[segment] || 'User';
  };

  if (loading && clients?.length === 0) {
    return (
      <div className="bg-card rounded-xl shadow-premium border border-border p-8 text-center">
        <p className="text-muted-foreground">Chargement des données CRM...</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Main CRM Panel */}
        <div className="bg-card rounded-xl shadow-premium border border-border">
          <div className="p-4 md:p-6 border-b border-border">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg md:text-xl font-bold text-foreground mb-1">CRM Intégré</h3>
                <p className="text-sm text-muted-foreground">
                  Gestion complète de la relation client
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Select
                  options={segmentOptions}
                  value={selectedSegment}
                  onChange={setSelectedSegment}
                  className="w-40"
                />
                <Button 
                  variant="default" 
                  iconName="UserPlus" 
                  iconPosition="left" 
                  size="sm"
                  onClick={() => handleOpenModal()}
                >
                  Nouveau Client
                </Button>
              </div>
            </div>
          </div>

          {error && (
            <div className="p-4 bg-error/10 border-b border-error/20">
              <p className="text-error text-sm">{error}</p>
            </div>
          )}

          <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients?.map((client) => (
                <div
                  key={client?.id}
                  className="bg-muted/50 rounded-xl p-4 hover:bg-muted transition-colors"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${getSegmentColor(client?.segment)}15` }}
                    >
                      <Icon
                        name={getSegmentIcon(client?.segment)}
                        size={24}
                        style={{ color: getSegmentColor(client?.segment) }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-foreground mb-1">{client?.name}</h4>
                      <p className="text-xs text-muted-foreground truncate">{client?.email}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Segment:</span>
                      <span
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{
                          backgroundColor: `${getSegmentColor(client?.segment)}15`,
                          color: getSegmentColor(client?.segment),
                        }}
                      >
                        {client?.segment?.toUpperCase()}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Valeur vie:</span>
                      <span className="font-semibold text-foreground">
                        ${client?.lifetime_value?.toFixed(2) || '0.00'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Projets:</span>
                      <span className="font-semibold text-foreground">
                        {client?.total_projects || 0}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Satisfaction:</span>
                      <div className="flex items-center gap-1">
                        <Icon name="Star" size={14} className="text-warning fill-warning" />
                        <span className="font-semibold text-foreground">
                          {client?.satisfaction_score?.toFixed(1) || '0.0'}/10
                        </span>
                      </div>
                    </div>

                    {client?.next_follow_up && (
                      <div className="pt-2 border-t border-border">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Icon name="Calendar" size={12} />
                          <span>
                            Suivi: {new Date(client?.next_follow_up)?.toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-2 pt-2">
                      <button
                        onClick={() => handleViewClient(client)}
                        className="flex-1 px-3 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-xs font-medium transition-colors"
                      >
                        Voir Détails
                      </button>
                      <button
                        onClick={() => handleOpenModal(client)}
                        className="w-9 h-9 rounded-lg bg-background hover:bg-primary/10 flex items-center justify-center transition-colors"
                      >
                        <Icon name="Edit" size={16} className="text-primary" />
                      </button>
                      <button
                        onClick={() => handleDeleteClient(client?.id)}
                        className="w-9 h-9 rounded-lg bg-background hover:bg-error/10 flex items-center justify-center transition-colors"
                      >
                        <Icon name="Trash2" size={16} className="text-error" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {clients?.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucun client trouvé</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Upcoming Follow-ups */}
        {upcomingFollowUps?.length > 0 && (
          <div className="bg-card rounded-xl shadow-premium border border-border">
            <div className="p-4 md:p-6 border-b border-border">
              <h3 className="text-lg font-bold text-foreground">Suivis à venir</h3>
            </div>
            <div className="p-4 md:p-6">
              <div className="space-y-3">
                {upcomingFollowUps?.slice(0, 5)?.map((client) => (
                  <div
                    key={client?.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Icon name="Calendar" size={20} className="text-primary" />
                      <div>
                        <p className="font-medium text-foreground">{client?.name}</p>
                        <p className="text-xs text-muted-foreground">{client?.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-foreground">
                        {new Date(client?.next_follow_up)?.toLocaleDateString('fr-FR')}
                      </p>
                      <p className="text-xs text-muted-foreground">{client?.service}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Client History Modal */}
        {selectedClient && clientHistory && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-premium max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-foreground">{selectedClient?.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{selectedClient?.email}</p>
                  </div>
                  <button
                    onClick={() => setSelectedClient(null)}
                    className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                  >
                    <Icon name="X" size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6">
                {/* Client Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-1">Valeur vie</p>
                    <p className="text-lg font-bold text-foreground">
                      ${selectedClient?.lifetime_value?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-1">Projets</p>
                    <p className="text-lg font-bold text-foreground">
                      {selectedClient?.total_projects || 0}
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-1">Devis</p>
                    <p className="text-lg font-bold text-foreground">
                      {clientHistory?.quotes?.length || 0}
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-1">Satisfaction</p>
                    <div className="flex items-center gap-1">
                      <Icon name="Star" size={16} className="text-warning fill-warning" />
                      <p className="text-lg font-bold text-foreground">
                        {selectedClient?.satisfaction_score?.toFixed(1) || '0.0'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Communications History */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-foreground mb-4">
                    Historique des communications
                  </h4>
                  <div className="space-y-3">
                    {clientHistory?.communications?.slice(0, 5)?.map((comm) => (
                      <div key={comm?.id} className="bg-muted/50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: 'var(--color-primary)15' }}
                          >
                            <Icon
                              name={
                                comm?.communicationType === 'email' ?'Mail'
                                  : comm?.communicationType === 'phone' ?'Phone'
                                  : comm?.communicationType === 'meeting' ?'Users' :'MessageSquare'
                              }
                              size={20}
                              className="text-primary"
                            />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <h5 className="font-semibold text-foreground">{comm?.subject}</h5>
                              <span className="text-xs text-muted-foreground">
                                {new Date(comm?.createdAt)?.toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">{comm?.message}</p>
                            {comm?.durationMinutes && (
                              <p className="text-xs text-muted-foreground mt-2">
                                Durée: {comm?.durationMinutes} minutes
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                    {clientHistory?.communications?.length === 0 && (
                      <div className="text-center py-8">
                        <Icon
                          name="MessageSquare"
                          size={32}
                          className="text-muted-foreground mx-auto mb-2"
                        />
                        <p className="text-sm text-muted-foreground">
                          Aucune communication enregistrée
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Satisfaction Scores */}
                {clientHistory?.satisfaction?.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-foreground mb-4">
                      Évaluations de satisfaction
                    </h4>
                    <div className="space-y-3">
                      {clientHistory?.satisfaction?.slice(0, 3)?.map((sat) => (
                        <div key={sat?.id} className="bg-muted/50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Icon name="Star" size={20} className="text-warning fill-warning" />
                              <span className="text-lg font-bold text-foreground">
                                {sat?.overallScore}/10
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(sat?.createdAt)?.toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{sat?.feedback}</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                            <div>
                              <span className="text-muted-foreground">Qualité:</span>
                              <span className="ml-1 font-medium text-foreground">
                                {sat?.serviceQualityScore}/10
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Communication:</span>
                              <span className="ml-1 font-medium text-foreground">
                                {sat?.communicationScore}/10
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Délais:</span>
                              <span className="ml-1 font-medium text-foreground">
                                {sat?.timelinessScore}/10
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Valeur:</span>
                              <span className="ml-1 font-medium text-foreground">
                                {sat?.valueScore}/10
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-premium max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-foreground">
                  {editingClient ? 'Modifier le Client' : 'Nouveau Client'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nom complet *"
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  required
                />
                <Input
                  label="Email *"
                  type="email"
                  value={formData?.email}
                  onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                  required
                />
                <Input
                  label="Téléphone"
                  value={formData?.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Segment *</label>
                  <Select
                    options={segmentOptions?.filter(opt => opt?.value !== 'all')}
                    value={formData?.segment}
                    onChange={(value) => setFormData({ ...formData, segment: value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
                <textarea
                  value={formData?.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                  rows={4}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <Button type="submit" variant="default" className="flex-1">
                  {editingClient ? 'Mettre à jour' : 'Créer'}
                </Button>
                <Button type="button" variant="outline" onClick={handleCloseModal} className="flex-1">
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default CRMPanel;